"use strict";
/**
 * SpotifyService.ts
 * Comprehensive Spotify integration for Meci.
 * Supports:
 * - Real Spotify OAuth 2.0 Web API authentication
 * - Spotify Profile fetching (Display name, Avatar, URL, Product type)
 * - Live currently-playing playback polling with progress & album art
 * - Manual Spotify username/URL link fallback
 * - Preferences sync with localStorage (show on profile, show as status)
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.spotifyService = void 0;
// Built-in public Spotify Client ID (standard web integration)
const DEFAULT_CLIENT_ID = '4a32ec4283894781b0a501e4a64ec3bf';
const SCOPES = [
    'user-read-currently-playing',
    'user-read-playback-state',
    'user-read-recently-played',
    'user-read-private',
    'user-read-email'
].join('%20');
class SpotifyService {
    constructor() {
        this.listeners = new Set();
        this.pollInterval = null;
        this.currentTrack = null;
        this.pollIntervalSeconds = 5;
        if (typeof window !== 'undefined') {
            this.handleOAuthCallback();
            this.setupOAuthListeners();
            this.loadRemoteConfig();
            this.refreshProfile();
            this.initBackgroundPoller();
        }
    }
    /**
     * Listen to OAuth popup window postMessage and storage events
     */
    setupOAuthListeners() {
        window.addEventListener('message', (event) => {
            if (event.data?.type === 'MECI_SPOTIFY_AUTH_SUCCESS') {
                const payload = event.data;
                if (payload.token) {
                    localStorage.setItem('meci_spotify_token', payload.token);
                    if (payload.refreshToken)
                        localStorage.setItem('meci_spotify_refresh_token', payload.refreshToken);
                    if (payload.profile) {
                        this.saveProfile(payload.profile);
                    }
                    this.refreshProfile().then(() => {
                        this.fetchCurrentTrack();
                        this.notify();
                    });
                }
            }
        });
        window.addEventListener('storage', (e) => {
            if (e.key === 'meci_spotify_token' || e.key === 'meci_spotify_connected') {
                this.refreshProfile().then(() => {
                    this.fetchCurrentTrack();
                    this.notify();
                });
            }
        });
    }
    /**
     * Refresh Spotify profile directly from Web API in browser
     */
    async refreshProfile() {
        const token = localStorage.getItem('meci_spotify_token');
        if (!token)
            return this.getProfile();
        try {
            const res = await fetch('https://api.spotify.com/v1/me', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const displayName = data.display_name || data.id || data.email?.split('@')[0] || 'Spotify';
                const profile = {
                    id: data.id || 'spotify_user',
                    displayName: displayName,
                    email: data.email,
                    avatar: (data.images && data.images.length > 0) ? data.images[0].url : '',
                    profileUrl: data.external_urls?.spotify || `https://open.spotify.com/user/${data.id}`,
                    product: data.product || 'premium',
                    token: token
                };
                this.saveProfile(profile);
                this.notify();
                return profile;
            }
            else if (res.status === 401) {
                console.warn('[Spotify] Token expired or invalid during refreshProfile, resetting session.');
                this.disconnect();
                return null;
            }
        }
        catch (e) {
            console.warn('[Spotify] Profile refresh error:', e);
        }
        return this.getProfile();
    }
    /**
     * Load integration settings configured in Admin panel
     */
    async loadRemoteConfig() {
        try {
            const res = await fetch('/api/v1/integrations/config');
            if (res.ok) {
                const data = await res.json();
                if (data && data.spotify) {
                    if (data.spotify.clientId) {
                        localStorage.setItem('meci_spotify_client_id', data.spotify.clientId);
                    }
                    if (data.spotify.redirectUri) {
                        localStorage.setItem('meci_spotify_redirect_uri', data.spotify.redirectUri);
                    }
                    if (data.spotify.pollInterval) {
                        this.pollIntervalSeconds = Number(data.spotify.pollInterval) || 5;
                        this.initBackgroundPoller();
                    }
                }
            }
        }
        catch (e) {
            // Offline fallback
        }
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    notify() {
        this.listeners.forEach(l => l());
    }
    /**
     * Check URL hash for Spotify OAuth token redirection (fallback)
     */
    handleOAuthCallback() {
        try {
            const hash = window.location.hash;
            if (hash && hash.includes('access_token=')) {
                const params = new URLSearchParams(hash.substring(1));
                const token = params.get('access_token');
                if (token) {
                    history.replaceState(null, '', window.location.pathname + window.location.search);
                    this.connectWithToken(token).then(() => {
                        console.log('[Spotify] Successfully authenticated via OAuth token!');
                    }).catch(err => {
                        console.error('[Spotify] OAuth profile error:', err);
                    });
                }
            }
        }
        catch (e) {
            console.warn('[Spotify] OAuth callback handler error:', e);
        }
    }
    /**
     * Launch Spotify OAuth Authorization with response_type=code
     */
    loginWithSpotify(customClientId) {
        const clientId = customClientId || localStorage.getItem('meci_spotify_client_id') || DEFAULT_CLIENT_ID;
        const defaultRedirect = `${window.location.origin}/oauth/spotify`;
        const rawRedirect = localStorage.getItem('meci_spotify_redirect_uri') || defaultRedirect;
        const redirectUri = encodeURIComponent(rawRedirect);
        const authUrl = `https://accounts.spotify.com/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&scope=${SCOPES}&show_dialog=true`;
        const width = 500;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const popup = window.open(authUrl, 'SpotifyLogin', `width=${width},height=${height},left=${left},top=${top},status=0,toolbar=0,menubar=0`);
        if (popup) {
            const checkClosed = setInterval(() => {
                try {
                    if (!popup || popup.closed) {
                        clearInterval(checkClosed);
                        setTimeout(() => {
                            if (this.isConnected()) {
                                this.fetchCurrentTrack();
                                this.notify();
                            }
                        }, 500);
                    }
                }
                catch {
                    // Ignore
                }
            }, 500);
        }
        else {
            window.location.href = authUrl;
        }
    }
    /**
     * Connect with Spotify Access Token and fetch real profile
     */
    async connectWithToken(token) {
        const res = await fetch('https://api.spotify.com/v1/me', {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
            throw new Error(`Spotify API Hatası: ${res.statusText}`);
        }
        const data = await res.json();
        const profile = {
            id: data.id || 'spotify_user',
            displayName: data.display_name || data.id || 'Spotify Kullanıcısı',
            email: data.email,
            avatar: data.images?.[0]?.url || '',
            profileUrl: data.external_urls?.spotify || `https://open.spotify.com/user/${data.id}`,
            product: data.product || 'free',
            token: token,
        };
        this.saveProfile(profile);
        this.fetchCurrentTrack();
        return profile;
    }
    /**
     * Connect manually with Spotify username or link
     */
    connectManual(usernameOrUrl) {
        const clean = usernameOrUrl.trim();
        if (!clean)
            return;
        let displayName = clean;
        let profileUrl = `https://open.spotify.com/user/${encodeURIComponent(clean)}`;
        if (clean.includes('open.spotify.com/user/')) {
            const match = clean.match(/user\/([^/?#]+)/);
            if (match) {
                displayName = decodeURIComponent(match[1]);
                profileUrl = clean;
            }
        }
        const profile = {
            id: displayName.toLowerCase().replace(/\s+/g, '_'),
            displayName: displayName,
            profileUrl: profileUrl,
            product: 'premium'
        };
        this.saveProfile(profile);
    }
    saveProfile(profile) {
        localStorage.setItem('meci_conn_spotify', profile.displayName);
        localStorage.setItem('meci_spotify_id', profile.id);
        localStorage.setItem('meci_spotify_url', profile.profileUrl);
        if (profile.avatar)
            localStorage.setItem('meci_spotify_avatar', profile.avatar);
        if (profile.token)
            localStorage.setItem('meci_spotify_token', profile.token);
        if (profile.product)
            localStorage.setItem('meci_spotify_product', profile.product);
        localStorage.setItem('meci_spotify_connected', 'true');
        // Default toggles on
        if (localStorage.getItem('meci_conn_spotify_show') === null) {
            localStorage.setItem('meci_conn_spotify_show', 'true');
        }
        if (localStorage.getItem('meci_spotify_show_status') === null) {
            localStorage.setItem('meci_spotify_show_status', 'true');
        }
        this.notify();
    }
    isConnected() {
        if (typeof window === 'undefined')
            return false;
        return !!(localStorage.getItem('meci_conn_spotify') || localStorage.getItem('meci_spotify_token'));
    }
    getProfile() {
        if (!this.isConnected())
            return null;
        const name = localStorage.getItem('meci_conn_spotify') || 'Spotify Kullanıcısı';
        return {
            id: localStorage.getItem('meci_spotify_id') || 'spotify_user',
            displayName: name,
            avatar: localStorage.getItem('meci_spotify_avatar') || '',
            profileUrl: localStorage.getItem('meci_spotify_url') || `https://open.spotify.com/user/${encodeURIComponent(name)}`,
            product: localStorage.getItem('meci_spotify_product') || 'premium',
            token: localStorage.getItem('meci_spotify_token') || undefined
        };
    }
    isShowOnProfile() {
        if (typeof window === 'undefined')
            return true;
        return localStorage.getItem('meci_conn_spotify_show') !== 'false';
    }
    setShowOnProfile(val) {
        localStorage.setItem('meci_conn_spotify_show', String(val));
        this.notify();
    }
    isShowStatus() {
        if (typeof window === 'undefined')
            return true;
        return localStorage.getItem('meci_spotify_show_status') !== 'false';
    }
    setShowStatus(val) {
        localStorage.setItem('meci_spotify_show_status', String(val));
        if (val && this.isConnected()) {
            this.fetchCurrentTrack();
        }
        this.notify();
    }
    disconnect() {
        localStorage.removeItem('meci_conn_spotify');
        localStorage.removeItem('meci_spotify_id');
        localStorage.removeItem('meci_spotify_url');
        localStorage.removeItem('meci_spotify_avatar');
        localStorage.removeItem('meci_spotify_token');
        localStorage.removeItem('meci_spotify_product');
        localStorage.removeItem('meci_spotify_connected');
        localStorage.removeItem('meci_spotify_current_track');
        this.currentTrack = null;
        this.notify();
    }
    /**
     * Fetch currently playing track from Spotify Web API
     */
    async fetchCurrentTrack() {
        const token = localStorage.getItem('meci_spotify_token');
        if (!token) {
            // If manual mode, check if there is a cached/simulated track
            const cached = localStorage.getItem('meci_spotify_current_track');
            if (cached) {
                try {
                    this.currentTrack = JSON.parse(cached);
                }
                catch { }
            }
            return this.currentTrack;
        }
        try {
            let res = await fetch('https://api.spotify.com/v1/me/player/currently-playing', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 401) {
                console.warn('[Spotify] Token expired or invalid, disconnecting.');
                this.disconnect();
                return null;
            }
            // If currently-playing returns 204 or not ok, try /player endpoint
            if (res.status === 204 || !res.ok) {
                try {
                    const playerRes = await fetch('https://api.spotify.com/v1/me/player', {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (playerRes.status === 401) {
                        console.warn('[Spotify] Token expired or invalid on player, disconnecting.');
                        this.disconnect();
                        return null;
                    }
                    if (playerRes.ok && playerRes.status === 200) {
                        res = playerRes;
                    }
                }
                catch { }
            }
            if (res.status === 204) {
                // O an çalan şarkı yoksa son dinlenen şarkıyı kontrol et
                try {
                    const recentRes = await fetch('https://api.spotify.com/v1/me/player/recently-played?limit=1', {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (recentRes.ok) {
                        const recentData = await recentRes.json();
                        const recentItem = recentData.items?.[0]?.track;
                        if (recentItem) {
                            const recentTrack = {
                                id: recentItem.id || '',
                                name: recentItem.name || '',
                                artist: (recentItem.artists || []).map((a) => a.name).join(', '),
                                album: recentItem.album?.name || '',
                                albumArt: (recentItem.album?.images && recentItem.album.images[0]) ? recentItem.album.images[0].url : '',
                                durationMs: recentItem.duration_ms || 0,
                                progressMs: 0,
                                isPlaying: false,
                                trackUrl: recentItem.external_urls?.spotify || '',
                                updatedAt: Date.now()
                            };
                            this.currentTrack = recentTrack;
                            localStorage.setItem('meci_spotify_current_track', JSON.stringify(recentTrack));
                            this.notify();
                            return recentTrack;
                        }
                    }
                }
                catch { }
                if (this.currentTrack) {
                    this.currentTrack = { ...this.currentTrack, isPlaying: false };
                    this.notify();
                    return this.currentTrack;
                }
                return null;
            }
            if (res.status > 299) {
                return this.currentTrack;
            }
            const data = await res.json();
            if (!data || !data.item) {
                if (this.currentTrack) {
                    this.currentTrack = { ...this.currentTrack, isPlaying: false };
                    this.notify();
                    return this.currentTrack;
                }
                return null;
            }
            const item = data.item;
            const track = {
                id: item.id || '',
                name: item.name || '',
                artist: (item.artists || []).map((a) => a.name).join(', '),
                album: item.album?.name || '',
                albumArt: (item.album?.images && item.album.images[0]) ? item.album.images[0].url : '',
                durationMs: item.duration_ms || 0,
                progressMs: data.progress_ms || 0,
                isPlaying: data.is_playing ?? true,
                trackUrl: item.external_urls?.spotify || '',
                updatedAt: Date.now()
            };
            this.currentTrack = track;
            localStorage.setItem('meci_spotify_current_track', JSON.stringify(track));
            this.notify();
            return track;
        }
        catch (e) {
            return this.currentTrack;
        }
    }
    getCurrentTrack() {
        if (!this.currentTrack && typeof window !== 'undefined') {
            const cached = localStorage.getItem('meci_spotify_current_track');
            if (cached) {
                try {
                    this.currentTrack = JSON.parse(cached);
                }
                catch { }
            }
        }
        return this.currentTrack;
    }
    /**
     * Set custom/demo track (useful when Spotify app is running or user wants to preview presence)
     */
    setCustomTrack(track) {
        if (!track) {
            this.currentTrack = null;
            localStorage.removeItem('meci_spotify_current_track');
        }
        else {
            this.currentTrack = {
                id: track.id || 'track_1',
                name: track.name || 'Starboy',
                artist: track.artist || 'The Weeknd, Daft Punk',
                album: track.album || 'Starboy',
                albumArt: track.albumArt || 'https://i.scdn.co/image/ab67616d0000b2734718e2b124f79258be7bc452',
                durationMs: track.durationMs || 230000,
                progressMs: track.progressMs || 84000,
                isPlaying: track.isPlaying ?? true,
                trackUrl: track.trackUrl || 'https://open.spotify.com',
                updatedAt: Date.now()
            };
            localStorage.setItem('meci_spotify_current_track', JSON.stringify(this.currentTrack));
        }
        this.notify();
    }
    initBackgroundPoller() {
        if (this.pollInterval)
            clearInterval(this.pollInterval);
        const ms = Math.max(3000, (this.pollIntervalSeconds || 5) * 1000);
        this.pollInterval = setInterval(() => {
            if (this.isConnected() && this.isShowStatus()) {
                this.fetchCurrentTrack();
            }
        }, ms);
    }
}
exports.spotifyService = new SpotifyService();
